import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export interface InlineRenameInputProps extends React.ComponentProps<'input'> {
  /** Classes for the wrapper span (layout constraints like `min-w-0`, `flex-1`). */
  containerClassName?: string
}

/**
 * Text input for inline renames that keeps the exact footprint of the text it
 * replaces, so flipping a label into rename mode (or typing in it) never
 * resizes the field.
 *
 * Callers pass the SAME typography classes the display element uses (font
 * size / weight / line-height); an invisible twin span carrying the draft
 * value sizes the box, and the input — borderless, unpadded, transparent —
 * is stacked over it on the same grid cell. Width therefore tracks the text
 * exactly and height is the text's own line box. The editing affordance is a
 * box-shadow underline, which does not participate in layout.
 *
 * `style` (e.g. a computed `fontSize`) is applied to the wrapper so the twin
 * and the input inherit identical font metrics.
 */
const InlineRenameInput = forwardRef<HTMLInputElement, InlineRenameInputProps>(
  ({ className, containerClassName, style, value, placeholder, ...props }, ref) => {
    return (
      <span
        className={cn('inline-grid min-w-0 max-w-full', containerClassName)}
        style={style}
      >
        <span
          aria-hidden
          className={cn(className, 'col-start-1 row-start-1 invisible whitespace-pre overflow-hidden min-w-[2ch]')}
        >
          {/* Strip CR/LF like <input type=text> does to its value, so the
              whitespace-pre twin can never go multiline when the input can't. */}
          {(String(value ?? '') || placeholder || ' ').replace(/[\r\n]+/g, '')}
        </span>
        <input
          ref={ref}
          type="text"
          value={value}
          placeholder={placeholder}
          className={cn(
            className,
            'col-start-1 row-start-1 w-full min-w-0 border-0 bg-transparent p-0 outline-none',
            'shadow-[0_1px_0_0_#f97316]',
          )}
          {...props}
        />
      </span>
    )
  },
)
InlineRenameInput.displayName = 'InlineRenameInput'

export default InlineRenameInput
